import { useEffect, useState } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { UserManagementApp } from '@aimsos/user-management';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the User Management application inside the Core Shell.
 * Mirrors the same embedding pattern as GovernanceEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where Auth0 SDK itself has no session.
 */
const UserMgmtEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [umI18nReady, setUmI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/user-management/i18n/register')
      .then(({ registerUserManagementI18n }) => {
        registerUserManagementI18n(registerNamespace);
        return i18n.loadNamespaces('user-management');
      })
      .then(() => {
        if (!cancelled) setUmI18nReady(true);
      })
      .catch((err) => console.error('[UserMgmtEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = { mounted: true };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !umI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <UserManagementApp />
    </ShellAuthProvider>
  );
};

export default UserMgmtEmbed;
